import { useMemo } from "react";
import { StyleSheet } from "react-native";
import Svg, { Circle } from "react-native-svg";

import { pictureScaleOf } from "../pictureScale";
import type { UiTheme } from "../theme";
import { dotAppearance, neutralDotAppearance } from "./chartDots";
import { wagmiPixelMapper, WAGMI_Y_GUTTER, type Layer, type WagmiChartData } from "./wagmiChartModel";

// Split out of `WagmiLineChart.tsx` (the 500-line cap). Hand-drawn per-point
// dots, layered ON TOP of the plot as a sibling of the Wagmi tree (never a
// descendant). The tap that reads the chart is `ChartTapLayer.tsx`.
//
// 🪤 `<LineChart.Dot>` does NOT render on web-rnw (verified via the visual
// bench, 2026-09-17): its `AnimatedCircle` is `Reanimated.createAnimatedComponent(Circle)`,
// and on this target the resulting DOM node lands in the HTML namespace
// instead of SVG's, `cx`/`cy` are set correctly but `r` computes to 0px, so
// every dot is a zero-size, invisible point. This isn't a per-point-count
// issue: even ONE dot (the old "now" accent) was silently gone before this
// fix. PG-45, 24/09, drawn with a plain `<Svg>`/`<Circle>` per point instead
// (a plain-Svg pattern already proven on web-rnw), positioned
// with the SAME linear x/y mapping wagmi's own `getPath` uses internally,
// see `wagmiChartModel.ts`'s `wagmiPixelMapper` for the formula and why
// `height` here is this chart's own `height` prop, not wagmi's inflated one.
// ⚠️ No `viewBox`: `width`/`height` are the actual pixel rect (same one the
// old `View` overlay covered), so `cx`/`cy` need no rescale.
//
// PG-46/PG-48 finding #5, `pxX`/`pxY` are kept as PLAIN functions (not their
// own `useMemo`/`useCallback`): they close over `x0`/`xEnd`/`width`/`lo`/`hi`,
// primitives already stable across a selection-only re-render, and the one
// thing that DOES need memoizing here is `dots` below, which lists them in
// its own dependency array by the primitives they read, not by these
// functions' own (recreated-every-render) identity.

// PG-42, Antenore 24/09: a series decimated to more than `width /
// DOT_DENSITY_DIVISOR` kept samples still draws as a solid smear of
// overlapping neutral dots even though the LINE reads fine at that density.
// Antenore 24/09: «sopra soglia restano il now e i pallini dopo un
// silenzio»: every OTHER neutral dot is dropped (25/09: a point whose kept
// `hasGap` is true, `WagmiChartData`'s own doc comment, survives density
// thinning same as the accent dot, it is the one that says "N days since
// the previous point"). Per-series, not per-point.
const DOT_DENSITY_DIVISOR = 4;

type Props = {
  layers: Layer[];
  data: WagmiChartData;
  theme: UiTheme;
  x0: number;
  xEnd: number;
  lo: number;
  hi: number;
  width: number;
  height: number;
  /** Left and right margin of the plot around `width` (a picture's). */
  inset?: number;
  /** The plot's own `yGutter`, scaled in a picture. */
  yGutter?: number;
};

// On a 1080 picture a dot is at least 14 px across.
const PICTURE_DOT_MIN_R = 7;

export function WagmiChartDots({
  layers,
  data,
  theme,
  x0,
  xEnd,
  lo,
  hi,
  width,
  height,
  inset = 0,
  yGutter = WAGMI_Y_GUTTER,
}: Props) {
  const { pxX, pxY } = wagmiPixelMapper({ x0, xEnd, lo, hi }, width, height, yGutter);
  // A picture at k px per point: dots and their spacing grow with it.
  const k = pictureScaleOf(theme);
  const radius = (size: number) => (k > 1 ? Math.max(PICTURE_DOT_MIN_R, size * k) : size);

  const dots = useMemo(
    () =>
      layers.flatMap((layer) => {
        const points = data[layer.id] ?? [];
        const dense = points.length > width / (DOT_DENSITY_DIVISOR * k);
        return points.flatMap((p, idx) => {
          // One layer is one whole series, so this really is the newest
          // reading, it used to be the last point of the last trunk, which
          // is not the same claim on a series that had been cut.
          const isAccentDot = idx === points.length - 1;
          // 25/09: a point that marks a real silence (`p.hasGap`, kept by
          // `decimateBuiltSeries` on purpose, see `WagmiChartData`'s own doc
          // comment) survives density thinning same as the accent dot.
          if (dense && !isAccentDot && !p.hasGap) return [];
          const appearance = isAccentDot ? dotAppearance(theme, layer) : neutralDotAppearance(theme);
          return [{ key: `${layer.id}-dot-${idx}`, cx: inset + pxX(p.timestamp), cy: pxY(p.value), ...appearance }];
        });
      }),
    // `pxX`/`pxY` are plain functions of these same primitives (see the
    // comment above): listing the primitives instead of the functions' own
    // (recreated-every-render) identity is what makes this memo hold steady
    // across a selection-only re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layers, data, theme, x0, xEnd, width, lo, hi, height, k, inset, yGutter],
  );

  if (width === 0) return null;

  return (
    <Svg width={width + 2 * inset} height={height} style={styles.dotsOverlay} pointerEvents="none">
      {dots.map((d) => {
        // ⚠️ A `View`'s `borderWidth` draws INSIDE `size` (the old radius);
        // an SVG `stroke` is centred ON the path. `r = size - borderWidth/2`
        // plus `strokeWidth = borderWidth` puts the OUTER edge back at
        // `size` either way, so the two renderers agree on the dot's
        // footprint, not just its centre.
        const size = radius(d.size);
        if (d.borderWidth === undefined) {
          return <Circle key={d.key} cx={d.cx} cy={d.cy} r={size} fill={d.color} />;
        }
        const borderWidth = d.borderWidth * k;
        return (
          <Circle
            key={d.key}
            cx={d.cx}
            cy={d.cy}
            r={size - borderWidth / 2}
            fill={d.color}
            stroke={d.borderColor}
            strokeWidth={borderWidth}
          />
        );
      })}
    </Svg>
  );
}

const styles = StyleSheet.create({
  // Covers the same rect as `WagmiLineChart`'s `plot`: absolute, pinned to
  // (0, 0), sized to `width` x `height` above, not stretched via
  // `right`/`bottom` (an `<Svg>` needs explicit pixel dimensions to size
  // itself, unlike the `View` it replaces).
  dotsOverlay: { position: "absolute", top: 0, left: 0 },
});
